import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  MapPin,
  LogOut,
  Users as UsersIcon,
  Clock,
  Navigation,
  X,
  Map as MapIcon,
  RefreshCw,
} from "lucide-react";

import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
} from "react-leaflet";

import L from "leaflet";
import "leaflet/dist/leaflet.css";

import api from "../constant/api";
import { toast } from "react-toastify";
import { useMenuPermission } from "../hooks/useMenuPermission";

const MENU_ID = 21;

// =====================================================
// FIX LEAFLET DEFAULT ICON
// =====================================================

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",

  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",

  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// =====================================================
// MAP AUTO FIT
// =====================================================

function MapAutoFit({ points }) {
  const map = useMap();

  useEffect(() => {
    if (!points || points.length === 0) return;

    if (points.length === 1) {
      map.setView(points[0], 16);
      return;
    }

    const bounds = L.latLngBounds(points);

    map.fitBounds(bounds, {
      padding: [40, 40],
    });
  }, [map, points]);

  return null;
}

// =====================================================
// GET TODAY
// =====================================================

const getToday = () => {
  return new Date().toISOString().slice(0, 10);
};

// =====================================================
// FORMAT DATE TIME
// =====================================================

const formatDateTime = (date) => {
  if (!date) return "—";

  const d = new Date(date);

  if (Number.isNaN(d.getTime())) {
    return "—";
  }

  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// =====================================================
// FORMAT TIME
// =====================================================

const formatTime = (date) => {
  if (!date) return "—";

  const d = new Date(date);

  if (Number.isNaN(d.getTime())) {
    return "—";
  }

  return d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

// =====================================================
// DURATION
// =====================================================

const formatDuration = (seconds) => {
  if (
    seconds === null ||
    seconds === undefined ||
    seconds === ""
  ) {
    return "—";
  }

  const totalSeconds = Math.max(
    0,
    Number(seconds)
  );

  const hours = Math.floor(
    totalSeconds / 3600
  );

  const minutes = Math.floor(
    (totalSeconds % 3600) / 60
  );

  const secs = Math.floor(
    totalSeconds % 60
  );

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }

  return `${secs}s`;
};

// =====================================================
// DISTANCE BETWEEN TWO LOCATIONS
// =====================================================

const getDistanceKm = (
  lat1,
  lng1,
  lat2,
  lng2
) => {
  if (
    lat1 === null ||
    lng1 === null ||
    lat2 === null ||
    lng2 === null ||
    lat1 === undefined ||
    lng1 === undefined ||
    lat2 === undefined ||
    lng2 === undefined
  ) {
    return null;
  }

  const R = 6371;

  const dLat =
    ((Number(lat2) - Number(lat1)) *
      Math.PI) /
    180;

  const dLng =
    ((Number(lng2) - Number(lng1)) *
      Math.PI) /
    180;

  const a =
    Math.sin(dLat / 2) *
      Math.sin(dLat / 2) +
    Math.cos(
      (Number(lat1) * Math.PI) / 180
    ) *
      Math.cos(
        (Number(lat2) * Math.PI) / 180
      ) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c =
    2 *
    Math.atan2(
      Math.sqrt(a),
      Math.sqrt(1 - a)
    );

  return R * c;
};

// =====================================================
// MAIN
// =====================================================

export default function CustomerVisit() {
  const { loading } =
    useMenuPermission(MENU_ID);

  // ===================================================
  // USER
  // ===================================================

  const user = JSON.parse(
    localStorage.getItem("user") || "{}"
  );

  const user_id = user.user_id;
  const role = user.role;

  const isAdmin =
    role === "admin" ||
    role === "superadmin";

  // ===================================================
  // STATES
  // ===================================================

  const [visits, setVisits] =
    useState([]);

  const [customers, setCustomers] =
    useState([]);

  const [showStart, setShowStart] =
    useState(false);

  const [showMap, setShowMap] =
    useState(false);

  const [selectedVisit, setSelectedVisit] =
    useState(null);

  const [customerSearch, setCustomerSearch] =
    useState("");

  const [selectedCustomer, setSelectedCustomer] =
    useState(null);

  const [purpose, setPurpose] =
    useState("");

  const [notes, setNotes] =
    useState("");

  const [busy, setBusy] =
    useState(false);

  const [loadingVisits, setLoadingVisits] =
    useState(false);

  // ===================================================
  // LOAD VISITS
  // ===================================================

  const loadVisits = async () => {
    if (
      user_id === undefined ||
      user_id === null ||
      user_id === ""
    ) {
      toast.error("User ID not found");
      return;
    }

    try {
      setLoadingVisits(true);

      const res = await api.get(
        `/Salesman/getVisits?user_id=${encodeURIComponent(
          user_id
        )}`
      );

      console.log(
        "GET VISITS:",
        res.data
      );

      if (res.data?.success) {
        setVisits(
          Array.isArray(res.data.data)
            ? res.data.data
            : []
        );
      } else {
        setVisits([]);

        toast.error(
          res.data?.msg ||
            "Failed to load visits"
        );
      }
    } catch (error) {
      console.log(
        "Load Visits Error:",
        error?.response?.data ||
          error
      );

      toast.error(
        error?.response?.data?.msg ||
          "Failed to load visits"
      );
    } finally {
      setLoadingVisits(false);
    }
  };

  // ===================================================
  // LOAD CUSTOMERS
  // ===================================================

  const loadCustomers = async () => {
    try {
      const res = await api.get(
        "/Product/getCustomers"
      );

      setCustomers(
        Array.isArray(res.data?.data)
          ? res.data.data
          : []
      );
    } catch (error) {
      console.log(
        "Customer Load Error:",
        error
      );

      toast.error(
        "Failed to load customers"
      );
    }
  };

  // ===================================================
  // INITIAL LOAD
  // ===================================================

  useEffect(() => {
    if (user_id) {
      loadVisits();
      loadCustomers();
    }
  }, [user_id]);

  // ===================================================
  // ACTIVE VISIT
  // ===================================================

  const activeVisit = useMemo(() => {
    if (isAdmin) return null;

    return visits.find(
      (v) =>
        v.status === "in_progress"
    );
  }, [visits, isAdmin]);

  // ===================================================
  // CUSTOMER SEARCH
  // ===================================================

  const filteredCustomers =
    customers.filter((c) => {
      const search =
        customerSearch
          .toLowerCase()
          .trim();

      if (!search) return true;

      return (
        c.name
          ?.toLowerCase()
          .includes(search) ||
        c.phone
          ?.toString()
          .includes(search)
      );
    });

  // ===================================================
  // GET LOCATION
  // ===================================================

  const getLocation = () =>
    new Promise((resolve) => {
      if (
        !navigator.geolocation
      ) {
        toast.error(
          "Geolocation is not supported"
        );

        resolve({
          lat: null,
          lng: null,
        });

        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat:
              position.coords
                .latitude,

            lng:
              position.coords
                .longitude,
          });
        },
        (error) => {
          console.log(
            "Location Error:",
            error
          );

          toast.warning(
            "Location permission denied. Visit will continue without location."
          );

          resolve({
            lat: null,
            lng: null,
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });

  // ===================================================
  // RESET START FORM
  // ===================================================

  const resetStartForm = () => {
    setSelectedCustomer(null);
    setCustomerSearch("");
    setPurpose("");
    setNotes("");
  };

  // ===================================================
  // START VISIT
  // ===================================================

  const handleStart = async () => {
    if (!user_id) {
      toast.error(
        "User ID not found"
      );
      return;
    }

    if (!selectedCustomer) {
      toast.error(
        "Select a customer first"
      );
      return;
    }

    if (activeVisit) {
      toast.error(
        "You already have an active visit"
      );
      return;
    }

    try {
      setBusy(true);

      const {
        lat,
        lng,
      } = await getLocation();

      const payload = {
        user_id: Number(user_id),

        customer_id:
          selectedCustomer.customer_id,

        purpose:
          purpose?.trim() || "",

        notes:
          notes?.trim() || "",

        lat,
        lng,
      };

      console.log(
        "CHECK IN PAYLOAD:",
        payload
      );

      const res = await api.post(
        "/Salesman/visitCheckIn",
        payload
      );

      console.log(
        "CHECK IN RESPONSE:",
        res.data
      );

      if (res.data?.success) {
        toast.success(
          "Visit started successfully ✅"
        );

        setShowStart(false);

        resetStartForm();

        await loadVisits();
      } else {
        toast.error(
          res.data?.msg ||
            "Failed to start visit"
        );
      }
    } catch (error) {
      console.log(
        "Check In Error:",
        error?.response?.data ||
          error
      );

      toast.error(
        error?.response?.data?.msg ||
          "Failed to start visit"
      );
    } finally {
      setBusy(false);
    }
  };

  // ===================================================
  // END VISIT
  // ===================================================

  const handleEnd = async (
    visitId
  ) => {
    if (!visitId) {
      toast.error(
        "Visit ID not found"
      );
      return;
    }

    if (!user_id) {
      toast.error(
        "User ID not found"
      );
      return;
    }

    try {
      setBusy(true);

      const {
        lat,
        lng,
      } = await getLocation();

      const payload = {
        user_id: Number(user_id),
        lat,
        lng,
        notes: "",
      };

      console.log(
        "CHECK OUT PAYLOAD:",
        payload
      );

      const res = await api.post(
        `/Salesman/visitCheckOut/${visitId}`,
        payload
      );

      console.log(
        "CHECK OUT RESPONSE:",
        res.data
      );

      if (res.data?.success) {
        toast.success(
          "Visit ended successfully ✅"
        );

        await loadVisits();
      } else {
        toast.error(
          res.data?.msg ||
            "Failed to end visit"
        );
      }
    } catch (error) {
      console.log(
        "Check Out Error:",
        error?.response?.data ||
          error
      );

      toast.error(
        error?.response?.data?.msg ||
          "Failed to end visit"
      );
    } finally {
      setBusy(false);
    }
  };

  // ===================================================
  // OPEN MAP
  // ===================================================

  const openMap = (visit) => {
    setSelectedVisit(visit);
    setShowMap(true);
  };

  // ===================================================
  // MAP POINTS
  // ===================================================

  const mapPoints = useMemo(() => {
    if (!selectedVisit) {
      return [];
    }

    const points = [];

    if (
      selectedVisit.check_in_lat !==
        null &&
      selectedVisit.check_in_lat !==
        undefined &&
      selectedVisit.check_in_lng !==
        null &&
      selectedVisit.check_in_lng !==
        undefined
    ) {
      points.push([
        Number(
          selectedVisit.check_in_lat
        ),
        Number(
          selectedVisit.check_in_lng
        ),
      ]);
    }

    if (
      selectedVisit.check_out_lat !==
        null &&
      selectedVisit.check_out_lat !==
        undefined &&
      selectedVisit.check_out_lng !==
        null &&
      selectedVisit.check_out_lng !==
        undefined
    ) {
      points.push([
        Number(
          selectedVisit.check_out_lat
        ),
        Number(
          selectedVisit.check_out_lng
        ),
      ]);
    }

    return points;
  }, [selectedVisit]);

  // ===================================================
  // DISTANCE
  // ===================================================

  const selectedDistance =
    selectedVisit
      ? getDistanceKm(
          selectedVisit.check_in_lat,
          selectedVisit.check_in_lng,
          selectedVisit.check_out_lat,
          selectedVisit.check_out_lng
        )
      : null;

  // ===================================================
  // PERMISSION LOADING
  // ===================================================

  if (loading) {
    return (
      <div className="p-6 text-gray-500">
        Checking permissions...
      </div>
    );
  }

  // ===================================================
  // UI
  // ===================================================

  return (
    <div className="w-full">

      {/* =================================================
          HEADER
      ================================================= */}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">

        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
            Customer Visit
          </h1>

          <p className="text-sm text-gray-500 mt-1">
            {isAdmin
              ? "Field visit log across the team"
              : "Check in when you arrive at a customer"}
          </p>
        </div>

        {!isAdmin &&
          !activeVisit && (
            <button
              onClick={() =>
                setShowStart(true)
              }
              className="
                hidden md:flex
                items-center gap-2
                bg-blue-600
                hover:bg-blue-700
                text-white
                px-5 py-3
                rounded-xl
                shadow-lg
                transition
              "
            >
              <Plus size={16} />

              Start Visit
            </button>
          )}

      </div>

      {/* =================================================
          REFRESH
      ================================================= */}

      <div className="flex justify-end mb-3">

        <button
          onClick={loadVisits}
          disabled={loadingVisits}
          className="
            flex
            items-center
            gap-2
            text-sm
            bg-white
            border
            border-gray-200
            px-3
            py-2
            rounded-xl
            text-gray-600
            hover:bg-gray-50
          "
        >
          <RefreshCw
            size={15}
            className={
              loadingVisits
                ? "animate-spin"
                : ""
            }
          />

          Refresh
        </button>

      </div>

      {/* =================================================
          ACTIVE VISIT
      ================================================= */}

      {activeVisit && (
        <div className="
          bg-gradient-to-r
          from-amber-500
          to-orange-500
          text-white
          rounded-2xl
          p-4
          mb-5
          shadow-lg
          flex
          items-center
          justify-between
          flex-wrap
          gap-3
        ">

          <div>

            <p className="text-xs opacity-80">
              Visit in progress
            </p>

            <p className="font-bold">
              {activeVisit.customer_name}
            </p>

            <p className="text-xs opacity-80 mt-1">
              Started{" "}
              {formatTime(
                activeVisit.check_in_time
              )}
            </p>

            <p className="text-xs opacity-80">
              Duration{" "}
              {formatDuration(
                activeVisit.duration_seconds
              )}
            </p>

          </div>

          <div className="flex gap-2">

            <button
              onClick={() =>
                openMap(activeVisit)
              }
              className="
                flex
                items-center
                gap-2
                bg-white/20
                text-white
                font-semibold
                px-3
                py-2.5
                rounded-xl
                border
                border-white/30
              "
            >
              <MapIcon size={16} />

              Map
            </button>

            <button
              onClick={() =>
                handleEnd(
                  activeVisit.id
                )
              }
              disabled={busy}
              className="
                flex
                items-center
                gap-2
                bg-white
                text-orange-700
                font-bold
                px-4
                py-2.5
                rounded-xl
                shadow
                active:scale-95
                transition
                disabled:opacity-50
              "
            >
              <LogOut size={16} />

              {busy
                ? "Ending..."
                : "End Visit"}
            </button>

          </div>

        </div>
      )}

      {/* =================================================
          MOBILE ADD
      ================================================= */}

      {!isAdmin &&
        !activeVisit && (
          <button
            onClick={() =>
              setShowStart(true)
            }
            className="
              fixed
              bottom-6
              right-5
              bg-blue-600
              hover:bg-blue-700
              text-white
              w-14
              h-14
              flex
              items-center
              justify-center
              rounded-full
              shadow-2xl
              md:hidden
              z-50
            "
          >
            <Plus size={18} />
          </button>
        )}

      {/* =================================================
          DESKTOP TABLE
      ================================================= */}

      <div className="
        hidden
        md:block
        overflow-x-auto
        bg-white
        rounded-2xl
        shadow
        border
        border-gray-100
      ">

        <table className="min-w-full text-sm">

          <thead className="
            bg-gradient-to-r
            from-gray-900
            to-gray-700
            text-white
          ">

            <tr>

              <th className="px-4 py-3 text-left">
                Customer
              </th>

              {isAdmin && (
                <th className="px-4 py-3 text-left">
                  Salesman
                </th>
              )}

              <th className="px-4 py-3 text-left">
                Purpose
              </th>

              <th className="px-4 py-3 text-center">
                Check In
              </th>

              <th className="px-4 py-3 text-center">
                Check Out
              </th>

              <th className="px-4 py-3 text-center">
                Duration
              </th>

              <th className="px-4 py-3 text-center">
                Distance
              </th>

              <th className="px-4 py-3 text-center">
                Status
              </th>

              <th className="px-4 py-3 text-center">
                Map
              </th>

            </tr>

          </thead>

          <tbody>

            {visits.map((v) => {

              const distance =
                getDistanceKm(
                  v.check_in_lat,
                  v.check_in_lng,
                  v.check_out_lat,
                  v.check_out_lng
                );

              return (
                <tr
                  key={v.id}
                  className="
                    border-b
                    hover:bg-gray-50
                    transition
                  "
                >

                  {/* CUSTOMER */}

                  <td className="px-4 py-3">

                    <p className="
                      font-semibold
                      text-gray-800
                    ">
                      {v.customer_name ||
                        "Unknown"}
                    </p>

                    <p className="
                      text-xs
                      text-gray-400
                    ">
                      {v.customer_phone ||
                        ""}
                    </p>

                  </td>

                  {/* SALESMAN */}

                  {isAdmin && (
                    <td className="px-4 py-3">

                      <p className="
                        font-medium
                        text-gray-800
                      ">
                        {v.salesman_name ||
                          "Unknown"}
                      </p>

                      <p className="
                        text-xs
                        text-gray-400
                      ">
                        ID: {v.admin_id}
                      </p>

                    </td>
                  )}

                  {/* PURPOSE */}

                  <td className="
                    px-4
                    py-3
                    text-gray-600
                    max-w-[220px]
                  ">
                    {v.purpose ||
                      "—"}
                  </td>

                  {/* CHECK IN */}

                  <td className="
                    px-4
                    py-3
                    text-center
                    text-xs
                  ">

                    <div className="
                      flex
                      flex-col
                      items-center
                    ">

                      <span className="
                        font-semibold
                        text-gray-700
                      ">
                        {formatDateTime(
                          v.check_in_time
                        )}
                      </span>

                      {v.check_in_lat &&
                        v.check_in_lng && (
                          <span className="
                            text-[10px]
                            text-blue-500
                            flex
                            items-center
                            gap-1
                            mt-1
                          ">
                            <MapPin
                              size={10}
                            />

                            Location saved
                          </span>
                        )}

                    </div>

                  </td>

                  {/* CHECK OUT */}

                  <td className="
                    px-4
                    py-3
                    text-center
                    text-xs
                  ">

                    {v.check_out_time
                      ? formatDateTime(
                          v.check_out_time
                        )
                      : "—"}

                  </td>

                  {/* DURATION */}

                  <td className="
                    px-4
                    py-3
                    text-center
                  ">

                    <span className="
                      inline-flex
                      items-center
                      gap-1
                      px-2
                      py-1
                      rounded-lg
                      bg-blue-50
                      text-blue-700
                      text-xs
                      font-semibold
                    ">

                      <Clock
                        size={12}
                      />

                      {formatDuration(
                        v.duration_seconds
                      )}

                    </span>

                  </td>

                  {/* DISTANCE */}

                  <td className="
                    px-4
                    py-3
                    text-center
                    text-xs
                  ">

                    {distance !== null
                      ? `${distance.toFixed(
                          2
                        )} km`
                      : "—"}

                  </td>

                  {/* STATUS */}

                  <td className="
                    px-4
                    py-3
                    text-center
                  ">

                    <span
                      className={`
                        px-2
                        py-1
                        rounded-full
                        text-xs
                        font-semibold
                        ${
                          v.status ===
                          "completed"
                            ? "bg-green-100 text-green-700"
                            : "bg-amber-100 text-amber-700"
                        }
                      `}
                    >
                      {v.status ===
                      "completed"
                        ? "Completed"
                        : "In progress"}
                    </span>

                  </td>

                  {/* MAP */}

                  <td className="
                    px-4
                    py-3
                    text-center
                  ">

                    {(v.check_in_lat ||
                      v.check_out_lat) ? (
                      <button
                        onClick={() =>
                          openMap(v)
                        }
                        className="
                          inline-flex
                          items-center
                          gap-1
                          bg-blue-600
                          hover:bg-blue-700
                          text-white
                          px-3
                          py-1.5
                          rounded-lg
                          text-xs
                          font-semibold
                        "
                      >
                        <Navigation
                          size={12}
                        />

                        Map
                      </button>
                    ) : (
                      "—"
                    )}

                  </td>

                </tr>
              );
            })}

            {visits.length === 0 && (
              <tr>

                <td
                  colSpan={
                    isAdmin
                      ? 9
                      : 8
                  }
                  className="
                    text-center
                    py-10
                    text-gray-400
                  "
                >
                  {loadingVisits
                    ? "Loading visits..."
                    : "No visits logged yet."}
                </td>

              </tr>
            )}

          </tbody>

        </table>

      </div>

      {/* =================================================
          MOBILE CARDS
      ================================================= */}

      <div className="
        md:hidden
        space-y-3
        pb-20
      ">

        {visits.map((v) => {

          const distance =
            getDistanceKm(
              v.check_in_lat,
              v.check_in_lng,
              v.check_out_lat,
              v.check_out_lng
            );

          return (
            <motion.div
              key={v.id}
              initial={{
                opacity: 0,
                y: 15,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              className="
                bg-white
                border
                border-gray-100
                rounded-2xl
                shadow-sm
                p-4
              "
            >

              <div className="
                flex
                justify-between
                items-start
                gap-2
              ">

                <div className="min-w-0">

                  <h3 className="
                    font-semibold
                    text-gray-800
                    text-sm
                    truncate
                  ">
                    {v.customer_name}
                  </h3>

                  <p className="
                    text-xs
                    text-gray-400
                  ">
                    {v.customer_phone}
                  </p>

                  {isAdmin && (
                    <p className="
                      text-xs
                      text-gray-400
                      mt-0.5
                    ">
                      By{" "}
                      {v.salesman_name ||
                        "Unknown"}
                    </p>
                  )}

                </div>

                <span
                  className={`
                    shrink-0
                    px-2
                    py-1
                    rounded-full
                    text-[10px]
                    font-semibold
                    ${
                      v.status ===
                      "completed"
                        ? "bg-green-100 text-green-700"
                        : "bg-amber-100 text-amber-700"
                    }
                  `}
                >
                  {v.status ===
                  "completed"
                    ? "Done"
                    : "Active"}
                </span>

              </div>

              {v.purpose && (
                <p className="
                  text-xs
                  text-gray-500
                  mt-2
                ">
                  {v.purpose}
                </p>
              )}

              <div className="
                grid
                grid-cols-2
                gap-2
                mt-3
              ">

                <div className="
                  bg-gray-50
                  rounded-xl
                  p-2
                ">

                  <p className="
                    text-[10px]
                    text-gray-400
                  ">
                    Check In
                  </p>

                  <p className="
                    text-xs
                    font-semibold
                    text-gray-700
                  ">
                    {formatTime(
                      v.check_in_time
                    )}
                  </p>

                </div>

                <div className="
                  bg-gray-50
                  rounded-xl
                  p-2
                ">

                  <p className="
                    text-[10px]
                    text-gray-400
                  ">
                    Check Out
                  </p>

                  <p className="
                    text-xs
                    font-semibold
                    text-gray-700
                  ">
                    {formatTime(
                      v.check_out_time
                    )}
                  </p>

                </div>

                <div className="
                  bg-blue-50
                  rounded-xl
                  p-2
                ">

                  <p className="
                    text-[10px]
                    text-blue-400
                  ">
                    Duration
                  </p>

                  <p className="
                    text-xs
                    font-bold
                    text-blue-700
                  ">
                    {formatDuration(
                      v.duration_seconds
                    )}
                  </p>

                </div>

                <div className="
                  bg-purple-50
                  rounded-xl
                  p-2
                ">

                  <p className="
                    text-[10px]
                    text-purple-400
                  ">
                    Distance
                  </p>

                  <p className="
                    text-xs
                    font-bold
                    text-purple-700
                  ">
                    {distance !== null
                      ? `${distance.toFixed(
                          2
                        )} km`
                      : "—"}
                  </p>

                </div>

              </div>

              <div className="
                flex
                gap-2
                mt-3
              ">

                {(v.check_in_lat ||
                  v.check_out_lat) && (
                  <button
                    onClick={() =>
                      openMap(v)
                    }
                    className="
                      flex-1
                      bg-blue-600
                      text-white
                      py-2
                      rounded-xl
                      text-xs
                      font-semibold
                      flex
                      items-center
                      justify-center
                      gap-2
                    "
                  >
                    <MapIcon
                      size={14}
                    />

                    View Map
                  </button>
                )}

              </div>

            </motion.div>
          );
        })}

        {visits.length === 0 && (
          <div className="
            text-center
            py-16
            text-gray-400
          ">

            <UsersIcon
              className="mx-auto mb-2"
              size={28}
            />

            No visits logged yet.

          </div>
        )}

      </div>

      {/* =================================================
          START VISIT MODAL
      ================================================= */}

      <AnimatePresence>

        {showStart && (
          <>

            {/* BACKDROP */}

            <motion.div
              className="
                fixed
                inset-0
                bg-black/50
                z-[90]
              "
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              exit={{
                opacity: 0,
              }}
              onClick={() =>
                !busy &&
                setShowStart(false)
              }
            />

            {/* MODAL */}

            <motion.div
              className="
                fixed
                inset-x-0
                bottom-0
                sm:inset-0
                sm:flex
                sm:items-center
                sm:justify-center
                z-[100]
              "
              initial={{
                y: "100%",
              }}
              animate={{
                y: 0,
              }}
              exit={{
                y: "100%",
              }}
              transition={{
                type: "spring",
                damping: 25,
                stiffness: 250,
              }}
            >

              <div className="
                bg-white
                w-full
                sm:max-w-md
                sm:mx-4
                rounded-t-[28px]
                sm:rounded-2xl
                shadow-2xl
                max-h-[92vh]
                flex
                flex-col
                overflow-hidden
              ">

                {/* HANDLE */}

                <div className="
                  flex
                  justify-center
                  pt-3
                  sm:hidden
                ">
                  <div className="
                    w-14
                    h-1.5
                    bg-gray-300
                    rounded-full
                  " />
                </div>

                {/* HEADER */}

                <div className="
                  px-5
                  pt-3
                  sm:pt-5
                  pb-3
                  border-b
                  border-gray-100
                ">

                  <div className="
                    flex
                    items-center
                    justify-between
                  ">

                    <h2 className="
                      text-lg
                      font-bold
                      text-gray-800
                    ">
                      Start Customer Visit
                    </h2>

                    <button
                      onClick={() =>
                        !busy &&
                        setShowStart(
                          false
                        )
                      }
                      className="
                        w-8
                        h-8
                        rounded-full
                        bg-gray-100
                        flex
                        items-center
                        justify-center
                      "
                    >
                      <X size={16} />
                    </button>

                  </div>

                </div>

                {/* BODY */}

                <div className="
                  p-5
                  space-y-3
                  overflow-y-auto
                  flex-1
                ">

                  {/* CUSTOMER */}

                  <div className="relative">

                    <label className="
                      text-xs
                      font-semibold
                      text-gray-500
                      mb-1
                      block
                    ">
                      Customer
                    </label>

                    <input
                      placeholder="
                        Search customer by name or phone...
                      "
                      value={
                        selectedCustomer
                          ? selectedCustomer.name
                          : customerSearch
                      }
                      onChange={(e) => {
                        setCustomerSearch(
                          e.target.value
                        );

                        setSelectedCustomer(
                          null
                        );
                      }}
                      className="
                        border
                        w-full
                        p-2.5
                        rounded-xl
                        outline-none
                        focus:ring-2
                        focus:ring-blue-500
                      "
                    />

                    {customerSearch &&
                      !selectedCustomer && (
                        <div className="
                          absolute
                          bg-white
                          border
                          w-full
                          max-h-48
                          overflow-auto
                          z-50
                          rounded-xl
                          shadow-lg
                          mt-1
                        ">

                          {filteredCustomers.map(
                            (c) => (
                              <div
                                key={
                                  c.customer_id
                                }
                                onClick={() => {
                                  setSelectedCustomer(
                                    c
                                  );

                                  setCustomerSearch(
                                    ""
                                  );
                                }}
                                className="
                                  p-2.5
                                  hover:bg-gray-100
                                  cursor-pointer
                                  text-sm
                                  border-b
                                  last:border-0
                                "
                              >
                                {c.name}
                                {" — "}
                                {c.phone}
                              </div>
                            )
                          )}

                          {filteredCustomers.length ===
                            0 && (
                            <div className="
                              p-2.5
                              text-sm
                              text-gray-400
                            ">
                              No customers found
                            </div>
                          )}

                        </div>
                      )}

                  </div>

                  {/* PURPOSE */}

                  <div>

                    <label className="
                      text-xs
                      font-semibold
                      text-gray-500
                      mb-1
                      block
                    ">
                      Purpose
                    </label>

                    <textarea
                      rows={3}
                      placeholder="
                        Purpose of visit
                      "
                      value={purpose}
                      onChange={(e) =>
                        setPurpose(
                          e.target.value
                        )
                      }
                      className="
                        border
                        w-full
                        p-2.5
                        rounded-xl
                        outline-none
                        resize-none
                        focus:ring-2
                        focus:ring-blue-500
                      "
                    />

                  </div>

                  {/* NOTES */}

                  <div>

                    <label className="
                      text-xs
                      font-semibold
                      text-gray-500
                      mb-1
                      block
                    ">
                      Notes
                    </label>

                    <textarea
                      rows={3}
                      placeholder="
                        Notes (optional)
                      "
                      value={notes}
                      onChange={(e) =>
                        setNotes(
                          e.target.value
                        )
                      }
                      className="
                        border
                        w-full
                        p-2.5
                        rounded-xl
                        outline-none
                        resize-none
                        focus:ring-2
                        focus:ring-blue-500
                      "
                    />

                  </div>

                  {/* LOCATION INFO */}

                  <div className="
                    bg-blue-50
                    border
                    border-blue-100
                    rounded-xl
                    p-3
                    flex
                    gap-2
                  ">

                    <MapPin
                      size={18}
                      className="
                        text-blue-600
                        shrink-0
                      "
                    />

                    <div>

                      <p className="
                        text-xs
                        font-semibold
                        text-blue-700
                      ">
                        Location tracking
                      </p>

                      <p className="
                        text-[11px]
                        text-blue-500
                        mt-0.5
                      ">
                        Your current location
                        will be saved at
                        check-in and
                        check-out.
                      </p>

                    </div>

                  </div>

                </div>

                {/* FOOTER */}

                <div className="
                  flex
                  gap-2
                  p-4
                  border-t
                  border-gray-100
                  bg-white
                ">

                  <button
                    onClick={
                      handleStart
                    }
                    disabled={busy}
                    className="
                      flex-1
                      bg-blue-600
                      hover:bg-blue-700
                      text-white
                      py-3
                      rounded-xl
                      font-semibold
                      active:scale-95
                      transition
                      disabled:opacity-50
                      flex
                      items-center
                      justify-center
                      gap-2
                    "
                  >

                    <MapPin
                      size={16}
                    />

                    {busy
                      ? "Starting..."
                      : "Check In"}

                  </button>

                  <button
                    onClick={() =>
                      !busy &&
                      setShowStart(false)
                    }
                    disabled={busy}
                    className="
                      flex-1
                      bg-gray-100
                      hover:bg-gray-200
                      text-gray-700
                      py-3
                      rounded-xl
                      font-semibold
                    "
                  >
                    Cancel
                  </button>

                </div>

              </div>

            </motion.div>

          </>
        )}

      </AnimatePresence>

      {/* =================================================
          MAP MODAL
      ================================================= */}

      <AnimatePresence>

        {showMap &&
          selectedVisit && (
            <>

              {/* BACKDROP */}

              <motion.div
                className="
                  fixed
                  inset-0
                  bg-black/60
                  z-[190]
                "
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: 1,
                }}
                exit={{
                  opacity: 0,
                }}
                onClick={() =>
                  setShowMap(false)
                }
              />

              {/* MAP MODAL */}

              <motion.div
                className="
                  fixed
                  inset-0
                  z-[200]
                  flex
                  items-center
                  justify-center
                  p-3
                  sm:p-6
                "
                initial={{
                  opacity: 0,
                  scale: 0.95,
                }}
                animate={{
                  opacity: 1,
                  scale: 1,
                }}
                exit={{
                  opacity: 0,
                  scale: 0.95,
                }}
              >

                <div className="
                  bg-white
                  w-full
                  max-w-5xl
                  h-[90vh]
                  rounded-2xl
                  shadow-2xl
                  overflow-hidden
                  flex
                  flex-col
                ">

                  {/* MAP HEADER */}

                  <div className="
                    px-4
                    py-3
                    border-b
                    flex
                    items-center
                    justify-between
                    gap-3
                  ">

                    <div>

                      <h2 className="
                        font-bold
                        text-gray-800
                      ">
                        Visit Map
                      </h2>

                      <p className="
                        text-xs
                        text-gray-500
                      ">
                        {selectedVisit.customer_name}
                      </p>

                    </div>

                    <button
                      onClick={() =>
                        setShowMap(false)
                      }
                      className="
                        w-9
                        h-9
                        rounded-full
                        bg-gray-100
                        flex
                        items-center
                        justify-center
                      "
                    >
                      <X size={18} />
                    </button>

                  </div>

                  {/* MAP */}

                  <div className="
                    flex-1
                    relative
                  ">

                    {mapPoints.length > 0 ? (

                      <MapContainer
                        center={
                          mapPoints[0]
                        }
                        zoom={15}
                        scrollWheelZoom={true}
                        className="
                          w-full
                          h-full
                        "
                      >

                        <TileLayer
                          attribution='&copy; OpenStreetMap contributors'
                          url="
                            https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png
                          "
                        />

                        <MapAutoFit
                          points={
                            mapPoints
                          }
                        />

                        {/* CHECK IN */}

                        {selectedVisit.check_in_lat &&
                          selectedVisit.check_in_lng && (
                            <Marker
                              position={[
                                Number(
                                  selectedVisit.check_in_lat
                                ),
                                Number(
                                  selectedVisit.check_in_lng
                                ),
                              ]}
                            >

                              <Popup>

                                <div className="
                                  text-sm
                                ">

                                  <strong>
                                    Check In
                                  </strong>

                                  <br />

                                  {formatDateTime(
                                    selectedVisit.check_in_time
                                  )}

                                </div>

                              </Popup>

                            </Marker>
                          )}

                        {/* CHECK OUT */}

                        {selectedVisit.check_out_lat &&
                          selectedVisit.check_out_lng && (
                            <Marker
                              position={[
                                Number(
                                  selectedVisit.check_out_lat
                                ),
                                Number(
                                  selectedVisit.check_out_lng
                                ),
                              ]}
                            >

                              <Popup>

                                <div className="
                                  text-sm
                                ">

                                  <strong>
                                    Check Out
                                  </strong>

                                  <br />

                                  {formatDateTime(
                                    selectedVisit.check_out_time
                                  )}

                                </div>

                              </Popup>

                            </Marker>
                          )}

                        {/* ROUTE */}

                        {mapPoints.length >=
                          2 && (
                          <Polyline
                            positions={
                              mapPoints
                            }
                          />
                        )}

                      </MapContainer>

                    ) : (

                      <div className="
                        h-full
                        flex
                        items-center
                        justify-center
                        text-gray-400
                      ">

                        <div className="
                          text-center
                        ">

                          <MapPin
                            size={40}
                            className="
                              mx-auto
                              mb-2
                            "
                          />

                          <p>
                            Location not
                            available
                          </p>

                        </div>

                      </div>

                    )}

                  </div>

                  {/* MAP FOOTER */}

                  <div className="
                    p-4
                    border-t
                    bg-white
                  ">

                    <div className="
                      grid
                      grid-cols-2
                      sm:grid-cols-4
                      gap-2
                    ">

                      <div className="
                        bg-blue-50
                        rounded-xl
                        p-3
                      ">

                        <p className="
                          text-[10px]
                          text-blue-500
                        ">
                          Check In
                        </p>

                        <p className="
                          text-xs
                          font-bold
                          text-blue-800
                        ">
                          {formatDateTime(
                            selectedVisit.check_in_time
                          )}
                        </p>

                      </div>

                      <div className="
                        bg-green-50
                        rounded-xl
                        p-3
                      ">

                        <p className="
                          text-[10px]
                          text-green-500
                        ">
                          Check Out
                        </p>

                        <p className="
                          text-xs
                          font-bold
                          text-green-800
                        ">
                          {formatDateTime(
                            selectedVisit.check_out_time
                          )}
                        </p>

                      </div>

                      <div className="
                        bg-purple-50
                        rounded-xl
                        p-3
                      ">

                        <p className="
                          text-[10px]
                          text-purple-500
                        ">
                          Duration
                        </p>

                        <p className="
                          text-xs
                          font-bold
                          text-purple-800
                        ">
                          {formatDuration(
                            selectedVisit.duration_seconds
                          )}
                        </p>

                      </div>

                      <div className="
                        bg-orange-50
                        rounded-xl
                        p-3
                      ">

                        <p className="
                          text-[10px]
                          text-orange-500
                        ">
                          Distance
                        </p>

                        <p className="
                          text-xs
                          font-bold
                          text-orange-800
                        ">
                          {selectedDistance !==
                          null
                            ? `${selectedDistance.toFixed(
                                2
                              )} km`
                            : "—"}
                        </p>

                      </div>

                    </div>

                  </div>

                </div>

              </motion.div>

            </>
          )}

      </AnimatePresence>

    </div>
  );
}